import { getJSON, getText } from '@storebrand-digital/fms-translations';
import {
  CarDetails,
  HouseContentsDetails,
  HouseDetails,
  InsuranceDetails,
  MotorcycleDetails,
  Risk,
} from 'Api/insurance';
import { HomeBuyerDetails } from 'Api/insurance/types/products/homeBuyer.types';
import { HomeSellerDetails } from 'Api/insurance/types/products/homeSeller.types';
import { HuseierneHouseContentsDetails } from 'Api/insurance/types/products/huseiernehouseContents.types';
import { AgreementCode } from 'Constants/agreementCodes.constants';
import { InsuranceStatus } from 'Constants/insuranceStatus.constants';
import { ProductName } from 'Constants/products.constants';
import { add, format, parse } from 'date-fns';
import {
  isAdvantageousCustomer,
  isAkaAgreement,
  isCollectiveDiscountCustomer,
  isCoopAgreement,
  isElbilforeningAgreement,
  isHuseierneAgreement,
  isNewDanskeBankAgreement,
} from 'Utils/agreementCodes/agreementCodes.utils';
import { getPriceFromCwp } from 'Utils/detailsCwp.utils';
import { formatMoney } from 'Utils/formatting/formatting.utils';
import { getAppText } from 'Utils/text';
import { isDefined } from 'Utils/type.utils';
import {
  DETAILED_SECONDARY_FALSE_FMS_KEY,
  DETAILED_SECONDARY_TRUE_FMS_KEY,
  FIRST_RISK_SUM_FALLBACK_FMS_KEY,
  OWN_CONTRIBUTION_FALLBACK_FMS_KEY,
  STANDARD_DATE_FORMAT,
} from '../insuranceDetailsSections.constants';
import {
  INSURANCE_EVALUATION_TYPE,
  LabelValuePair,
  LevelCodes,
} from '../insuranceDetailsSections.types';
import { getOwnContributionValueFromRisk } from './insuranceDetailsSections.helpers';

export const getHomeInsuranceValidity = (
  insurance: HomeBuyerDetails | HomeSellerDetails,
): string => {
  const { outerIssueDate } = insurance;
  const endDate = insurance.policyEndDate;
  return getText('detailed.primary.homeInsurance.status', {
    insert: { validFromDate: outerIssueDate, endDate },
  });
};

export function getInsuranceValidity(insurance: InsuranceDetails): string {
  const { agreementProduct, outerIssueDate, isInRenewal } = insurance;
  if (agreementProduct === AgreementCode.EXTERNAL_PARTNER_FINN) {
    /* calculate endDate from issueDate, 30 days in time */
    const currentIssueDate = parse(
      outerIssueDate,
      STANDARD_DATE_FORMAT,
      new Date(),
    );
    const endDate = format(
      add(currentIssueDate, { years: 0, months: 0, days: 30 }),
      STANDARD_DATE_FORMAT,
    );
    return getText('detailed.primary.finnCarInsurance.status', {
      insert: { issueDate: outerIssueDate, endDate },
    });
  }

  if (isInRenewal) {
    if (insurance.insuranceStatus === InsuranceStatus.UNDER_CHANGE) {
      return getText(
        'detailed.primary.insuranceValidity.underChangeAndIsInRenewal',
      );
    }
    const status = getInsuranceStatus(insurance);
    const inRenewal = getText('detailed.primary.insuranceValidity.isInRenewal');
    return `${status} ${inRenewal}`;
  }
  return getInsuranceStatus(insurance);
}

function getInsuranceStatus(insurance: InsuranceDetails): string {
  switch (insurance.insuranceStatus) {
    case InsuranceStatus.ACTIVE: {
      return getText('detailed.primary.insuranceValidity.active', {
        insert: { validFromDate: insurance.validFromDate },
      });
    }
    case InsuranceStatus.UNDER_CHANGE: {
      return getText('detailed.primary.insuranceValidity.underChange');
    }
    case InsuranceStatus.UNDER_ACTIVATION: {
      return getText('detailed.primary.insuranceValidity.validFrom', {
        insert: { validFromDate: insurance.validFromDate },
      });
    }
    case InsuranceStatus.UNDER_CANCELLATION: {
      return getText('detailed.primary.insuranceValidity.underResignation');
    }
    case InsuranceStatus.CANCELLED: {
      return getText('detailed.primary.insuranceValidity.inactive');
    }
    default:
      return getText('detailed.primary.insuranceValidity.fallback');
  }
}

export function getBonus(insurance: CarDetails | MotorcycleDetails): string {
  const { bonus } = insurance;
  const allCodes = getJSON<Array<LevelCodes>>(
    'detailed.primary.bonus.levelCodes',
  );
  const currentBonus = allCodes.find((i) => i.levelCode === bonus?.levelCode);
  if (currentBonus === undefined || currentBonus === null) {
    return getText(OWN_CONTRIBUTION_FALLBACK_FMS_KEY);
  }
  return currentBonus.value;
}

export function getPriceInsuranceDetails(
  insurance: InsuranceDetails,
  fmsKey: string,
  cwpDetailsToggle: boolean,
): string {
  const { monthlyPriceToday, yearlyPriceToday } = getPriceFromCwp(insurance);

  return getText(fmsKey, {
    insert: {
      monthlyPrice: cwpDetailsToggle
        ? formatMoney(monthlyPriceToday)
        : formatMoney(insurance.monthlyCost),
      space: `\u00ad \u00ad \u00ad`,
      yearlyPrice: cwpDetailsToggle
        ? formatMoney(yearlyPriceToday)
        : formatMoney(insurance.annualCost),
    },
  });
}

export function getDiscount(
  agreementProduct: AgreementCode,
): string | undefined {
  if (isAdvantageousCustomer(agreementProduct)) {
    return getText('detailed.primary.discount.advantageous');
  }
  if (isCollectiveDiscountCustomer(agreementProduct)) {
    return getText('detailed.primary.discount.collective');
  }
  if (isAkaAgreement(agreementProduct)) {
    return getText('detailed.primary.discount.akademikerne');
  }
  if (isCoopAgreement(agreementProduct)) {
    return getText('detailed.primary.discount.coop');
  }
  if (isNewDanskeBankAgreement(agreementProduct)) {
    return getText('detailed.primary.discount.danskeBank');
  }
  if (isHuseierneAgreement(agreementProduct)) {
    return getText('detailed.primary.discount.huseierne');
  }
  if (isElbilforeningAgreement(agreementProduct)) {
    return getText('detailed.primary.discount.elbilforening');
  }
  /* return undefined because detailed page shall not map discount in primary for other insurances */
  return undefined;
}

export function removeValueFromTitle(title: string): string {
  return title.split(',')[0];
}

export function getValueFromTitle(title: string): number | undefined {
  const value = title.split(',');
  try {
    return Number(value[value.length - 1]);
  } catch (e) {
    /* */
  }
  return undefined;
}

export function setOwnContribution(
  ownContribution: number | undefined,
  risks?: Risk[],
): LabelValuePair {
  const ownContributionValueFromRisk = risks
    ? getOwnContributionValueFromRisk(risks)
    : undefined;

  const ownContributionValue = ownContribution ?? ownContributionValueFromRisk;

  return {
    label: getText('detailed.secondary.ownContribution.label'),
    value: ownContributionValue
      ? getText('detailed.secondary.ownContribution.value', {
          insert: {
            ownContribution: formatMoney(ownContributionValue),
          },
        })
      : getText(OWN_CONTRIBUTION_FALLBACK_FMS_KEY),
  };
}

export function setFirstRiskSum(
  firstRiskSum: number | undefined,
): LabelValuePair {
  return {
    label: getText('detailed.secondary.firstRiskSum.label'),
    value: isDefined(firstRiskSum)
      ? getText('detailed.secondary.firstRiskSum.value', {
          insert: { firstRiskSum: formatMoney(firstRiskSum) },
        })
      : getText(FIRST_RISK_SUM_FALLBACK_FMS_KEY),
  };
}

export function isInsuranceEvaluationFirstRisk(
  insuranceEvaluationType: string | undefined,
): boolean {
  return insuranceEvaluationType === INSURANCE_EVALUATION_TYPE.FIRST_RISK;
}

export function getLabelValuePairForBooleanInsuranceField(
  label: string,
  value: boolean | undefined,
): LabelValuePair {
  const yesOrNoString = getYesOrNoStringFromFMS(value);

  return {
    label: getText(`detailed.secondary.${label}.label`),
    value: yesOrNoString,
  };
}

export const getRentalValue = (insurance: HouseDetails): string | undefined => {
  const { hasRental, hasPartialRental } = insurance;
  const noRental = !hasRental && !hasPartialRental;

  if (noRental) {
    return getAppText('detailed.secondary.noRental.value');
  }

  if (hasRental && !hasPartialRental) {
    return getAppText('detailed.secondary.hasRental.value');
  }

  if (hasPartialRental && !hasRental) {
    return getAppText('detailed.secondary.hasPartialRental.value');
  }
  return undefined;
};

export const getYesOrNoStringFromFMS = (
  value: boolean | undefined,
): string | undefined => {
  if (value === undefined) {
    return undefined;
  }
  return value
    ? getText(DETAILED_SECONDARY_TRUE_FMS_KEY)
    : getText(DETAILED_SECONDARY_FALSE_FMS_KEY);
};

export function setSmokerInfo(smoker: boolean): LabelValuePair {
  const smokerValueLabel = getYesOrNoStringFromFMS(smoker);
  return {
    label: getText('detailed.secondary.smoker.label'),
    value: smokerValueLabel,
  };
}

export const getHouseContentsCoverageValue = (
  insurance: HouseContentsDetails | HuseierneHouseContentsDetails,
): string => {
  if (insurance.insuranceProduct === ProductName.HOUSE_CONTENTS_STB_AGREEMENT) {
    return getText('detailed.secondary.coverage.super');
  }
  return getText(
    `detailed.secondary.coverage.${insurance.coverage.replace(/\s/g, '')}`,
    {
      insert: { coverage: insurance.coverage },
    },
  );
};

export function checkHasFireClause(hasFireClause: boolean): string | undefined {
  if (hasFireClause) {
    return getText('detailed.secondary.infoTag.hasFireClause');
  }
  return undefined;
}
